import React, { useState, useEffect } from "react";
import * as translator from "../../../../utils/translator";
import { useCartContext } from "../../../../customHooks/useCart";

interface CartItemV25Props {
  product: ProductItem;
  onRemove: (product: ProductItem) => void;
  handleIncrement: (productItem: ProductItem) => void;
  handleDecrement: (productItem: ProductItem) => void;
  language: string;
  country: string;
  isIncrementDisabled?: boolean;
  riskGroup?: RiskGroupSummary;
  onRiskGroupConfirmationChange?: (confirmed: boolean) => void;
  insideRiskGroup?: boolean;
  showRiskGroupWarning?: boolean;
  riskGroupConfirmationDisabled?: boolean;
}

const MinusSVG = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6H10" stroke="#707885" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const PlusSVG = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 2V10M2 6H10" stroke="#707885" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const TrashSVG = () => (
  <svg className="w-[16px] h-[16px]" viewBox="0 0 16 16" fill="none">
    <path
      d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CartItemV25: React.FC<CartItemV25Props> = ({
  product,
  onRemove,
  handleIncrement,
  handleDecrement,
  language,
  country,
  isIncrementDisabled = false,
  riskGroup,
  onRiskGroupConfirmationChange,
  insideRiskGroup = false,
  showRiskGroupWarning = false,
  riskGroupConfirmationDisabled = false,
}) => {
  const localeID = `${language}-${country}`;
  const { getCart } = useCartContext();
  const [quantity, setQuantity] = useState<number>(parseInt(product.quantity));
  const showRiskGroup = Boolean(showRiskGroupWarning && riskGroup && riskGroup.status !== 'clear');

  useEffect(() => {
    setQuantity(parseInt(product.quantity));
  }, [product.quantity]);


  const handleDecrease = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (quantity > 1) {
      await handleDecrement(product);
    }
  };

  const handleIncrease = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!isIncrementDisabled) {
      await handleIncrement(product);
    }
  };

  const handleRemove = async () => {
    await onRemove(product);
  };

  // Test-lab kits get the stepper only when several kits per order are allowed (per-country switch, overridden by the A/B arm).
  const showStepper = product.type === "free_drug" && (!product.is_test_lab || getCart().allow_multiple_test_lab_kits === true);
  const title = translator.translate(language, "shipping", "riskGroups/soft_limit_title")?.replace("{group_name}", riskGroup?.name || "");
  const description = translator.translate(language, "shipping", "riskGroups/soft_limit_description")?.replace("{threshold}", String(riskGroup?.soft_threshold || ""));
  const checkboxLabel = translator.translate(language, "shipping", "riskGroups/confirmation");
  const hardMessage = translator.translate(language, "shipping", "riskGroups/hard_limit")?.replace("{group_name}", riskGroup?.name || "");
  const hardLimitExceeded = riskGroup?.status === 'hard_limit_exceeded';

  return (
    <div>
      <div id={`cart-item-${product.id}`} className={`flex gap-[8px] bg-white ${insideRiskGroup ? 'p-[8px]' : 'border-b border-[#e5e7eb] p-[8px]'}`}>
        <img className={`w-[100px] h-[82px] object-contain shrink-0 ${product.type !== "free_drug" ? "self-center" : ""}`} src={product.imgSrc} alt="" />
        <div className="flex-1 flex flex-col gap-[8px]">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-0 lg:gap-[8px]">
            <div className="flex flex-col">
              <p className="font-roboto text-[16px] font-medium text-[#1a1a2e] leading-[21px]">{product.title}</p>
              <p className="font-roboto text-[14px] font-normal text-[#999999] leading-[1.2]">{translator.translate(language, "shipping", "otcCartPopup/package")}: {product.custom_quantity_title}</p>
            </div>
            <div className="flex flex-col items-start lg:items-end lg:w-[115px] lg:min-w-[102px] lg:shrink-0">
              {product.price > 0 && <div className="flex gap-[6px] items-center leading-[19px]">
                {product.price_before_discount && <p className="font-roboto text-[13px] text-red-600 line-through">€{product.price_before_discount?.toFixed(2).replace(".", ",")}</p>}
                <p className="font-roboto text-[16px] font-bold text-[#1a1a2e]">{translator.getPrice(product.price * quantity, localeID)}</p>
              </div>}
              <p className="font-roboto text-[12px] text-[#999999]">{quantity > 1 ? `${translator.getPrice(product.price, localeID)} x ${quantity}` : " "}</p>
            </div>
          </div>
          <div className="flex items-end justify-between">
            {showStepper && <div className="flex items-center bg-white border border-solid border-[#e5e7eb] p-[4px] rounded-[6px] gap-[18px]">
              <button id="decrement-button" type="button" className={`w-[32px] h-[32px] p-[4px] rounded-[4px] bg-[#f5f5f5] border-0 flex items-center justify-center ${quantity === 1 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`} onClick={handleDecrease}><MinusSVG /></button>
              <span id="counter-value" className="text-[#1a1a2e] font-roboto text-[17px] leading-[25px] min-w-[20px] text-center">{quantity}</span>
              <button id="increment-button" type="button" className={`w-[32px] h-[32px] p-[4px] rounded-[4px] bg-[#f5f5f5] border-0 flex items-center justify-center ${isIncrementDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`} disabled={isIncrementDisabled} onClick={handleIncrease}><PlusSVG /></button>
            </div>}
            <button id="trash-btn" type="button" className={`border border-solid border-[#e5e7eb] flex items-center justify-center w-[32px] h-[32px] rounded-[4px] cursor-pointer p-[4px] flex-shrink-0 hover:bg-[#f5f5f5] ${!showStepper ? "ml-auto" : ""}`} onClick={handleRemove}><TrashSVG /></button>
          </div>
        </div>
      </div>
      {showRiskGroup && <div className="mt-[8px] flex flex-col gap-[10px] rounded-[14px] bg-[#ffeaea] px-[14px] py-[14px] text-[13px] leading-[18px]">
        <div className="flex items-center gap-[8px]">
          <svg className="h-[16px] w-[16px] shrink-0 text-[#c73535]" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.25" />
            <path d="M8 7v4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
            <circle cx="8" cy="4.75" r=".75" fill="currentColor" />
          </svg>
          <p className="font-medium text-[#c73535]">{hardLimitExceeded ? hardMessage : title}</p>
        </div>
        {!hardLimitExceeded && (
          <>
            <p className="font-semibold text-[#0D2C54]">{description}</p>
            <label className={`flex items-center gap-[10px] text-[#2E3E67] ${riskGroupConfirmationDisabled ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}>
              <input type="checkbox" className="h-[18px] w-[18px] shrink-0 cursor-pointer accent-[#707885]" checked={Boolean(riskGroup?.confirmed)} disabled={riskGroupConfirmationDisabled} onChange={(event) => onRiskGroupConfirmationChange?.(event.target.checked)} />
              <span>{checkboxLabel}</span>
            </label>
          </>
        )}
      </div>}
    </div>
  );
};

export default CartItemV25;
